import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RotateCw,
  ChevronLeft,
  ChevronRight,
  BookCopy,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { trpc } from '../../../lib/trpc';
import { Button, Badge, TimeAgo } from '../../atoms';
import { cn } from '../../../lib/utils';

// Pagination constants
const ITEMS_PER_PAGE = 50;

export interface EmailLogsTableProps {
  limit?: number;
  className?: string;
  showRetryButton?: boolean;
  onEmailLogClick?: (emailLogId: string) => void;
  isLoading?: boolean; // Allow parent to control loading state
  useCursorPagination?: boolean; // Use cursor-based pagination instead of offset
  enableInfiniteScroll?: boolean; // Enable infinite scrolling
}

export const EmailLogsTable: React.FC<EmailLogsTableProps> = ({
  limit: _limit = 10,
  className,
  showRetryButton = true,
  onEmailLogClick,
  isLoading: externalLoading = false,
  useCursorPagination = false,
  enableInfiniteScroll: _enableInfiniteScroll = false,
}) => {
  const [sortBy, setSortBy] = useState<'emailReceivedAt' | 'status' | 'source'>('emailReceivedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [cursor, setCursor] = useState<string | undefined>();
  const [retryingEmailLogId, setRetryingEmailLogId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get today's date range (start of day to end of day) - memoized for performance
  const todayDateRange = useMemo(() => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999
    );
    return { startOfDay, endOfDay };
  }, []);

  const { data, isLoading, error, refetch } = trpc.emailLogs.getEmailLogs.useQuery(
    {
      limit: ITEMS_PER_PAGE,
      offset: useCursorPagination ? 0 : (currentPage - 1) * ITEMS_PER_PAGE,
      cursor: useCursorPagination ? cursor : undefined,
      useCursor: useCursorPagination,
      startDate: todayDateRange.startOfDay.toISOString(),
      endDate: todayDateRange.endOfDay.toISOString(),
    },
    {
      staleTime: 30000, // 30 seconds - data is considered fresh for 30 seconds
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    }
  );

  const retryMutation = trpc.emailLogs.retryProcessing.useMutation({
    onSuccess: () => {
      toast.success('Email processing retry initiated');
      refetch();
      setRetryingEmailLogId(null);
    },
    onError: (error: any) => {
      toast.error(`Failed to retry processing: ${error.message}`);
      setRetryingEmailLogId(null);
    },
  });

  // All hooks must be called before any early returns
  // Sort data locally if needed
  const sortedLogs = data?.logs
    ? [...data.logs].sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
          case 'emailReceivedAt':
            comparison =
              new Date(a.emailReceivedAt).getTime() - new Date(b.emailReceivedAt).getTime();
            break;
          case 'status':
            comparison = a.status.localeCompare(b.status);
            break;
          case 'source':
            comparison = a.source.localeCompare(b.source);
            break;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
      })
    : [];

  // Server-side pagination logic
  const totalPages = useCursorPagination
    ? 1
    : data?.totalCount
      ? Math.ceil(data.totalCount / ITEMS_PER_PAGE)
      : 1;
  const startIndex = useCursorPagination ? 0 : (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = useCursorPagination
    ? sortedLogs.length
    : Math.min(startIndex + ITEMS_PER_PAGE, data?.totalCount || 0);
  const paginatedLogs = sortedLogs; // Server already returns paginated data
  const hasNextPage = useCursorPagination ? data?.hasMore : currentPage < totalPages;
  const nextCursor = data?.nextCursor;

  // Reset to page 1 when data structure changes (but not when just paginating)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const handleRetry = async (emailLogId: string) => {
    try {
      setRetryingEmailLogId(emailLogId);
      await retryMutation.mutateAsync({ emailLogId });
    } catch (error: any) {
      // Error is handled by the mutation's onError
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetch();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleViewDetails = (emailLogId: string) => {
    if (onEmailLogClick) {
      onEmailLogClick(emailLogId);
    }
  };

  const handleSort = (column: 'emailReceivedAt' | 'status' | 'source') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Use external loading state if provided, otherwise use internal loading
  const isCurrentlyLoading = externalLoading || isLoading;
  const isTableLoading = isCurrentlyLoading || isRefreshing;

  if (isCurrentlyLoading && !data) {
    return (
      <div className={cn('text-center py-8 text-gray-500', className)}>
        <div className="text-sm">Loading email logs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('text-center py-8', className)}>
        <p className="text-red-600 mb-4">Failed to load email logs</p>
        <Button onClick={() => refetch()} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  if (!data?.logs || data.logs.length === 0) {
    return (
      <div className={cn('text-center py-8 text-gray-500', className)}>
        <div className="text-4xl mb-4">📧</div>
        <p className="text-lg font-medium mb-2">No emails processed today</p>
        <p className="text-sm">
          Today's email processing logs will appear here once emails are received and processed.
        </p>
      </div>
    );
  }

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 ml-1" />
    );
  };

  // Map email processing status to Badge variants with count-based overrides
  const getStatusBadge = (
    status: string,
    processed: number = 0,
    skipped: number = 0,
    failed: number = 0
  ) => {
    // Count-based overrides (take precedence over base status)
    if (failed > 0) {
      return (
        <Badge variant="error" size="sm">
          ❌ Failed
        </Badge>
      );
    }

    if (processed === 0 && skipped > 0) {
      return (
        <Badge variant="warning" size="sm">
          <BookCopy className="w-3 h-3 mr-1" />
          Duplicate (Skipped)
        </Badge>
      );
    }

    if (processed === 0 && skipped === 0 && failed === 0) {
      // Special case: when status='processing' and all counts are zero, show Processing (not Failed)
      if (status === 'processing') {
        return (
          <Badge variant="warning" size="sm">
            ⏳ Processing
          </Badge>
        );
      }
      return (
        <Badge variant="error" size="sm">
          ❌ Failed
        </Badge>
      );
    }

    if (processed > 0 && skipped > 0) {
      return (
        <Badge variant="success" size="sm">
          ✅ Completed
        </Badge>
      );
    }

    // Base status mapping
    const statusMap = {
      received: { variant: 'info' as const, label: '📧 Received' },
      processing: { variant: 'warning' as const, label: '⏳ Processing' },
      completed: { variant: 'success' as const, label: '✅ Completed' },
      failed: { variant: 'error' as const, label: '❌ Failed' },
    };

    const config = statusMap[status as keyof typeof statusMap] || {
      variant: 'default' as const,
      label: status,
    };
    return (
      <Badge variant={config.variant} size="sm">
        {config.label}
      </Badge>
    );
  };

  // Compute results badge with priority: All Zero > Failed > Skipped > Processed
  const getResultsBadge = (processed: number, skipped: number, failed: number, status: string) => {
    // Special case: when status='processing' and all counts are zero, show 'Pending' amber badge
    if (processed === 0 && skipped === 0 && failed === 0 && status === 'processing') {
      return {
        variant: 'warning' as const,
        label: 'Pending',
        className: 'bg-yellow-100 text-yellow-800',
      };
    }

    // Special case: when all counts are zero (and not processing status), show as failed
    if (processed === 0 && skipped === 0 && failed === 0) {
      return {
        variant: 'error' as const,
        label: 'Failed',
        className: 'bg-red-100 text-red-800',
      };
    } else if (failed > 0) {
      return {
        variant: 'error' as const,
        label: `${failed} failed`,
        className: 'bg-red-100 text-red-800',
      };
    } else if (skipped > 0) {
      return {
        variant: 'warning' as const,
        label: `${processed} processed (${skipped} skipped)`,
        className: 'bg-yellow-100 text-yellow-800',
      };
    } else {
      return {
        variant: 'success' as const,
        label: `${processed} processed`,
        className: 'bg-green-100 text-green-800',
      };
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    if (useCursorPagination) {
      if (nextCursor) {
        setCursor(nextCursor);
      }
    } else {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Today Email Processing</h3>
        <div className="flex items-center space-x-3">
          <div
            className={cn(
              'text-sm text-gray-500 transition-opacity duration-200',
              isTableLoading && 'opacity-60'
            )}
          >
            {data.totalCount} email{data.totalCount !== 1 ? 's' : ''} today
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isTableLoading}
            className="p-1"
            title="Refresh email logs"
          >
            <RotateCw
              className={cn(
                'w-4 h-4 transition-transform duration-200',
                isTableLoading && 'animate-spin'
              )}
            />
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto relative">
        <div className="inline-block min-w-full align-middle">
          <div
            className={cn(
              'overflow-hidden border border-gray-200 rounded-lg transition-opacity duration-200',
              isTableLoading && 'opacity-50'
            )}
          >
            <table className="min-w-full divide-y divide-gray-200">
              {/* Table Header */}
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center">
                      From
                      <SortIcon column="source" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    To
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Received
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Attachments
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Results
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <SortIcon column="status" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedLogs.map((emailLog: any) => {
                  const canRetry = emailLog.status === 'failed' || emailLog.status === 'processing';
                  const hasErrors = emailLog.invoicesFailed > 0 || emailLog.errorMessage;
                  const showActions = true; // Always show actions to allow viewing details

                  return (
                    <tr key={emailLog.id} className="hover:bg-gray-50">
                      {/* From */}
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 max-w-48 truncate">
                          {emailLog.source}
                        </div>
                      </td>

                      {/* To */}
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500 max-w-48 truncate">
                          {emailLog.destination.join(', ')}
                        </div>
                      </td>

                      {/* Received */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        <TimeAgo date={new Date(emailLog.emailReceivedAt)} />
                      </td>

                      {/* Attachments */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {emailLog.attachmentCount}
                      </td>

                      {/* Results */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {(() => {
                          const result = getResultsBadge(
                            emailLog.invoicesProcessed,
                            emailLog.invoicesSkipped,
                            emailLog.invoicesFailed,
                            emailLog.status
                          );
                          return (
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-1 rounded-full text-xs',
                                result.className
                              )}
                            >
                              {result.label}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(
                          emailLog.status,
                          emailLog.invoicesProcessed,
                          emailLog.invoicesSkipped,
                          emailLog.invoicesFailed
                        )}
                        {hasErrors && (
                          <div className="text-xs text-red-600 mt-1">
                            {emailLog.errorMessage ? 'Error' : 'Some failed'}
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        {showActions && (
                          <div className="flex gap-1 justify-end">
                            {onEmailLogClick && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetails(emailLog.id)}
                                className="p-1"
                                title="View Details"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            {canRetry &&
                              showRetryButton &&
                              (emailLog.status === 'failed' ||
                                emailLog.status === 'processing') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRetry(emailLog.id)}
                                  className={cn(
                                    'p-1 transition-all duration-200',
                                    retryingEmailLogId === emailLog.id &&
                                      'opacity-70 cursor-not-allowed'
                                  )}
                                  title={
                                    retryingEmailLogId === emailLog.id
                                      ? 'Retrying...'
                                      : emailLog.status === 'processing'
                                        ? 'Retry Processing (Stuck)'
                                        : 'Retry Processing'
                                  }
                                  disabled={retryingEmailLogId === emailLog.id}
                                >
                                  <RotateCw
                                    className={cn(
                                      'w-4 h-4 transition-transform duration-200',
                                      retryingEmailLogId === emailLog.id && 'animate-spin'
                                    )}
                                  />
                                </Button>
                              )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Loading Overlay */}
        {isTableLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-2 text-gray-600">
              <RotateCw className="w-5 h-5 animate-spin" />
              <span className="text-sm font-medium">Refreshing...</span>
            </div>
          </div>
        )}

        {/* Pagination */}
        {(totalPages > 1 || (useCursorPagination && hasNextPage)) && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between items-center">
              <div>
                {useCursorPagination ? (
                  <p className="text-sm text-gray-700">
                    Showing {sortedLogs.length} emails{hasNextPage ? ' (more available)' : ''}
                  </p>
                ) : (
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{endIndex}</span> of{' '}
                    <span className="font-medium">{data?.totalCount || 0}</span> emails today
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`relative inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                {useCursorPagination ? (
                  <button
                    onClick={handleNextPage}
                    disabled={!hasNextPage}
                    className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Load More
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Retry Status */}
      {retryMutation.isLoading && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-700">Retrying email processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLogsTable;
